/**
 * Acceso a apiSiesa desde el navegador, a través del servidor del CRM.
 *
 * El navegador ya NO habla con apiSiesa. Antes lo hacía con un token que venía
 * incrustado en el bundle: cualquiera podía sacarlo del JavaScript y consultar
 * apiSiesa por su cuenta. Ahora ese token vive solo en el servidor, y aquí se
 * viaja con la sesión del propio usuario.
 *
 * El servidor solo acepta los endpoints y parámetros declarados en
 * server/api/services/SiesaProxyService.js.
 */

const getApiBase = (): string =>
  String(process.env.UMI_APP_API_URL || '').replace(/\/$/, '');

const getSessionToken = (): string => {
  try {
    const raw = localStorage.getItem('session');
    if (raw) {
      const session = JSON.parse(raw);
      if (session?.token) return String(session.token);
    }
  } catch {
    // sesión ilegible: se cae al token suelto
  }
  return localStorage.getItem('token') || '';
};

export type SiesaEndpoint =
  | 'accounting-book'
  | 'crm/items'
  | 'crm/pending-orders'
  | 'crm/stock'
  | 'get-extensions-by-reference'
  | 'get-fabrics'
  | 'get-item-extension-data'
  | 'get-item-price'
  | 'get-items'
  | 'get-location-by-warehouse'
  | 'get-order'
  | 'get-warehouses';

export type SiesaParams = Record<string, string | number | undefined | null>;

/**
 * Consulta un endpoint de apiSiesa por el proxy. Devuelve el cuerpo tal como lo
 * entrega apiSiesa (el proxy no lo transforma), así que los servicios que ya
 * normalizaban la respuesta siguen funcionando igual.
 *
 * Lanza si la sesión no sirve o si el servidor rechaza la consulta.
 */
export async function siesaGet<T = any>(endpoint: SiesaEndpoint, params: SiesaParams = {}): Promise<T> {
  const base = getApiBase();
  if (!base) throw new Error('URL del API del CRM no configurada');

  const search = new URLSearchParams();
  search.append('endpoint', endpoint);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, String(value));
  });

  const response = await fetch(`${base}/siesa-proxy?${search.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getSessionToken()}`,
    },
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.message || '';
    } catch {
      // cuerpo no JSON
    }
    throw new Error(detail || `Error consultando ${endpoint} (${response.status})`);
  }

  const body = await response.json();
  // El controlador responde { data, message, status }; se devuelve el data.
  return (body && typeof body === 'object' && 'data' in body ? body.data : body) as T;
}

/**
 * Igual que siesaGet pero devuelve [] en vez de lanzar. Para las pantallas donde
 * "no hay datos" y "falló la consulta" se dibujan igual y no vale la pena
 * romper el render.
 */
export async function siesaGetList<T = any>(endpoint: SiesaEndpoint, params: SiesaParams = {}): Promise<T[]> {
  try {
    const data = await siesaGet<any>(endpoint, params);
    return Array.isArray(data) ? (data as T[]) : [];
  } catch (error) {
    console.error(`[SiesaProxy] ${endpoint}:`, error);
    return [];
  }
}

export default { siesaGet, siesaGetList };
